import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";

import { v4Date, v4Money, v4Quantity } from "../v4Explanation";

const statuses = {
  pending: "Aguardando a janela",
  inconclusive: "Inconclusiva",
  within_expectation: "Dentro do esperado",
  outside_expectation: "Fora do esperado"
};
const directions = {
  reinforce: "Reforçar",
  revise: "Rever",
  await_evidence: "Aguardar evidência"
};
const nextActions = {
  wait_for_window_end: "Aguardar o fim da janela",
  collect_or_reconcile_own_SKUs:
    "Coletar ou reconciliar as porções deste produto",
  quantify_next_expectation: "Quantificar a próxima expectativa",
  attest_orders_catalog_scope: "Confirmar o catálogo dos pedidos",
  attest_source_coverage: "Confirmar a cobertura da fonte",
  confirm_human_application_window:
    "Confirmar a aplicação humana durante a janela",
  use_measured_result_in_next_range:
    "Usar o resultado medido na próxima faixa esperada",
  inspect_collection_provenance: "Conferir a origem da coleta"
};
const metrics = {
  units_sold_packages: "Embalagens em pedidos registrados",
  observed_revenue_brl: "Receita registrada na janela",
  subtotal_after_cmc_brl: "Receita registrada menos CMC na janela"
};
const reasons = {
  orders_catalog_scope_unconfirmed:
    "A fonte de pedidos não confirma o catálogo.",
  application_not_confirmed_for_full_window:
    "Aplicação não confirmada durante toda a janela.",
  zero_registered_without_source_coverage_attestation:
    "Nenhum pedido registrado; a completude da fonte não foi confirmada.",
  observations_unavailable_or_incompatible:
    "Observações ausentes ou incompatíveis com a janela.",
  expectation_not_quantified:
    "Sem estimativa suficiente para testar a hipótese."
};
const value = (metric, n) =>
  metric === "units_sold_packages" ? v4Quantity(n) : v4Money(n);
const rows = v => (Array.isArray(v) ? v : []);

export default function V4HypothesisReview({ suggestion }) {
  const current = rows(suggestion.hypothesis_evaluation).length
    ? suggestion.hypothesis_evaluation
    : rows(suggestion.hypotheses);
  const history = rows(suggestion.hypothesis_history);
  const reassessment = rows(suggestion.reassessment);
  return (
    <Box>
      {current.length ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Hipóteses desta decisão
          </Typography>
          <Box style={{ overflowX: "auto" }}>
            <Table
              size="small"
              aria-label="Hipóteses e resultados V4"
              style={{ minWidth: 480 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Hipótese</TableCell>
                  <TableCell>Esperado</TableCell>
                  <TableCell>Observado</TableCell>
                  <TableCell>Avaliação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {current.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      {h.statement}
                      <Typography variant="caption" display="block">
                        {metrics[h.metric]}
                        {h.condition === "selected_vector_applied"
                          ? " · Depende de aplicação confirmada"
                          : " · Cenário observado"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {h.minimum == null
                        ? "Sem estimativa suficiente"
                        : `${value(h.metric, h.minimum)} a ${value(
                            h.metric,
                            h.maximum
                          )}`}
                    </TableCell>
                    <TableCell>{value(h.metric, h.observed_value)}</TableCell>
                    <TableCell>
                      {statuses[h.status] || "Aguardando resultado"}
                      {reasons[h.reason_code] ? (
                        <Typography variant="caption" display="block">
                          {reasons[h.reason_code]}
                        </Typography>
                      ) : null}
                      {typeof h.next_action?.detail === "string" ? (
                        <Typography variant="caption" display="block">
                          Próximo passo: {h.next_action.detail}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {suggestion.followup?.window_start ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              Janela: {v4Date(suggestion.followup.window_start)} a{" "}
              {v4Date(suggestion.followup.window_end)}.
            </Typography>
          ) : null}
        </>
      ) : (
        <Typography variant="body2">
          Esta análise anterior não registrou uma hipótese mensurável.
        </Typography>
      )}
      {history.map(h => {
        const assessment = reassessment.find(
          r => r.execution_id === h.execution_id
        );
        return (
          <Box mt={2} key={h.execution_id}>
            <Typography variant="subtitle2">
              Decisão anterior · {v4Date(h.known_at)}
            </Typography>
            {rows(h.evaluation).map((e, i) => (
              <Typography variant="body2" paragraph key={i}>
                {e.statement} · {statuses[e.status] || "Pendente"}.
                {e.minimum != null
                  ? ` Esperado: ${value(e.metric, e.minimum)} a ${value(
                      e.metric,
                      e.maximum
                    )}.`
                  : ""}
                {reasons[e.reason_code] ? ` ${reasons[e.reason_code]}` : ""}
                {e.observed_value != null
                  ? ` Observado: ${value(e.metric, e.observed_value)}.`
                  : ""}
                {e.condition === "selected_vector_applied" &&
                !e.application_confirmation &&
                e.reason_code !== "application_not_confirmed_for_full_window"
                  ? " Aplicação não confirmada durante toda a janela."
                  : ""}
                {e.window_start
                  ? ` Janela: ${v4Date(e.window_start)} a ${v4Date(
                      e.window_end
                    )}.`
                  : ""}
                {typeof e.next_action?.detail === "string"
                  ? ` Próximo passo: ${e.next_action.detail}`
                  : ""}
              </Typography>
            ))}
            {assessment ? (
              <Box>
                <Typography variant="body2">
                  <strong>{directions[assessment.direction]}:</strong>{" "}
                  {assessment.reason}
                </Typography>
                {nextActions[assessment.next_action] ? (
                  <Typography variant="caption" display="block">
                    Próximo passo: {nextActions[assessment.next_action]}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      })}
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginTop: 12 }}
      >
        Os valores são pedidos registrados no período, sujeitos a alterações de
        status; não confirmam receita paga ou entrega final. Resultado observado
        e aplicação confirmada não demonstram efeito causal do preço.
      </Typography>
    </Box>
  );
}
